import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { setAccessToken } from "../store/authStore";

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const { refreshUser, verifyMagicLink } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      try {
        const token = searchParams.get("token");
        const id = searchParams.get("id");
        const accessToken = searchParams.get("access_token");

        let user = null;

        if (token && id) {
          // Magic link: token + id — call verifyMagicLink which calls completeAuth
          // completeAuth sets loginInProgressRef so the initial getMe() failure
          // cannot wipe the user we're about to set.
          user = await verifyMagicLink(id, token);
        } else if (accessToken) {
          setAccessToken(accessToken);
          user = await refreshUser();
        } else if (token) {
          setAccessToken(token);
          user = await refreshUser();
        } else {
          setError("Invalid sign-in link. Please try again.");
          return;
        }

        if (user) {
          // Small tick to let React flush the user state into context
          // before the route guard renders on "/".
          await new Promise((r) => setTimeout(r, 0));
          navigate("/", { replace: true });
        } else {
          setError("Sign-in failed. Please try again.");
        }
      } catch (err) {
        console.error("AuthCallback error:", err);
        setError(
          "Sign-in failed. The link may have expired. Please try again.",
        );
      }
    };

    run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6">
        <div
          className="w-full max-w-md border-[4px] border-on-surface bg-surface-container p-8 flex flex-col gap-6"
          style={{ boxShadow: "6px 6px 0px 0px #000" }}
        >
          <div className="flex items-center gap-3 text-error">
            <span
              className="material-symbols-outlined text-[28px]"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              error
            </span>
            <h1 className="font-extrabold text-xl uppercase tracking-tight">
              Sign-in Failed
            </h1>
          </div>
          <p className="text-on-surface-variant text-sm leading-relaxed">
            {error}
          </p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="w-full py-4 border-[3px] border-on-surface bg-primary-container text-on-primary-container font-bold uppercase tracking-widest text-sm active:translate-x-[2px] active:translate-y-[2px] transition-all"
            style={{ boxShadow: "4px 4px 0px 0px #000" }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        <div
          className="w-16 h-16 bg-primary-container border-[3px] border-on-surface overflow-hidden"
          style={{ boxShadow: "4px 4px 0px 0px #000" }}
        >
          <img
            src="/favicon.jpg"
            alt="Chicago"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="w-8 h-8 border-[4px] border-on-surface border-t-primary-container animate-spin" />
        <p className="font-bold uppercase tracking-widest text-sm text-on-surface-variant">
          Signing you in…
        </p>
      </div>
    </div>
  );
}
